import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Search, Boxes, Database, Eye, Plus, ChevronRight, ChevronDown, Loader2, AlertCircle, X } from "lucide-react";

// Module-level cache so re-mounting the Builder doesn't refetch.
const TABLES_BY_CONN = new Map();   // conn_id -> [{schema, name}]
const COLUMNS_CACHE = new Map();    // `${conn_id}:${table}` -> [cols]

const TAB_DATASETS = "datasets";
const TAB_TABLES = "tables";

export default function DataExplorer({
  datasets = [],
  onPickDimension,
  onPickMeasure,
  onCreateDatasetFromTable,
  activeDatasetId,
}) {
  const [tab, setTab] = useState(TAB_DATASETS);
  const [search, setSearch] = useState("");

  // Datasets tab
  const [expandedDs, setExpandedDs] = useState(activeDatasetId || null);
  const [previewing, setPreviewing] = useState(null);

  // Source tables tab
  const [conns, setConns] = useState([]);
  const [conn, setConn] = useState("");
  const [tables, setTables] = useState([]);
  const [tablesLoading, setTablesLoading] = useState(false);
  const [tablesError, setTablesError] = useState("");
  const [expandedTbl, setExpandedTbl] = useState(null);
  const [colsByTable, setColsByTable] = useState({});
  const [colsLoading, setColsLoading] = useState({}); // table -> bool
  const [colsError, setColsError] = useState({});

  useEffect(() => {
    api.get("/connections").then((r) => {
      const list = (r.data.connections || []).filter((c) => c.source_type !== "demo");
      setConns(list);
      if (list.length === 1) setConn(list[0].id);
    });
  }, []);

  useEffect(() => {
    if (!conn) { setTables([]); return; }
    if (TABLES_BY_CONN.has(conn)) {
      setTables(TABLES_BY_CONN.get(conn));
      setTablesError("");
      return;
    }
    setTablesLoading(true); setTablesError("");
    api.get(`/connections/${conn}/schema`, { params: { tables_only: true } })
      .then((r) => {
        const flat = (r.data?.schemas || []).flatMap((s) => (s.tables || []).map((t) => ({ schema: s.name, name: t.name })));
        TABLES_BY_CONN.set(conn, flat);
        setTables(flat);
      })
      .catch((e) => setTablesError(e?.response?.data?.detail || e.message))
      .finally(() => setTablesLoading(false));
  }, [conn]);

  const filteredDatasets = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return datasets;
    return datasets.filter((d) => (d.name || "").toLowerCase().includes(q));
  }, [datasets, search]);

  const filteredTables = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tables;
    return tables.filter((t) => t.name.toLowerCase().includes(q));
  }, [tables, search]);

  const loadColumns = async (tableName, schema) => {
    const key = `${conn}:${tableName}`;
    if (COLUMNS_CACHE.has(key)) {
      setColsByTable((s) => ({ ...s, [tableName]: COLUMNS_CACHE.get(key) }));
      return;
    }
    setColsLoading((s) => ({ ...s, [tableName]: true }));
    setColsError((s) => ({ ...s, [tableName]: "" }));
    try {
      const r = await api.get(`/connections/${conn}/tables/${encodeURIComponent(tableName)}/columns`, { params: schema ? { schema } : {} });
      const cols = r.data?.columns || [];
      COLUMNS_CACHE.set(key, cols);
      setColsByTable((s) => ({ ...s, [tableName]: cols }));
    } catch (e) {
      setColsError((s) => ({ ...s, [tableName]: e?.response?.data?.detail || e.message }));
    } finally {
      setColsLoading((s) => ({ ...s, [tableName]: false }));
    }
  };

  const showDatasetPreview = async (d) => {
    setPreviewing({ kind: "dataset", item: d, loading: true });
    try {
      const r = await api.post("/widget/data", { widget: { id: "_prev", type: "table", dataset_id: d.id || `demo:${d.demo_key}`, limit: 50 }, filters: [] });
      setPreviewing({ kind: "dataset", item: d, data: r.data });
    } catch (e) {
      setPreviewing({ kind: "dataset", item: d, error: e?.response?.data?.detail || e.message });
    }
  };

  const showTablePreview = async (t) => {
    setPreviewing({ kind: "table", item: t, loading: true });
    try {
      const r = await api.get(`/connections/${conn}/tables/${encodeURIComponent(t.name)}/sample`, { params: { limit: 50, ...(t.schema ? { schema: t.schema } : {}) } });
      setPreviewing({ kind: "table", item: t, data: r.data });
    } catch (e) {
      setPreviewing({ kind: "table", item: t, error: e?.response?.data?.detail || e.message });
    }
  };

  return (
    <div className="h-full flex flex-col" data-testid="data-explorer">
      {/* Tabs */}
      <div className="flex gap-1 mb-2">
        <button
          className={`text-[12px] px-2.5 py-1 rounded ${tab === TAB_DATASETS ? "bg-slate-900 text-white" : "bg-slate-100"}`}
          onClick={() => setTab(TAB_DATASETS)}
          data-testid="dx-tab-datasets"
        >Datasets</button>
        <button
          className={`text-[12px] px-2.5 py-1 rounded ${tab === TAB_TABLES ? "bg-slate-900 text-white" : "bg-slate-100"}`}
          onClick={() => setTab(TAB_TABLES)}
          data-testid="dx-tab-tables"
        >Source tables</button>
      </div>

      {/* Search */}
      <div className="relative mb-2">
        <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2" style={{ color: "var(--c-text-muted)" }} />
        <input
          className="input pl-7"
          placeholder={tab === TAB_DATASETS ? "Search datasets…" : "Search tables…"}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="dx-search"
        />
      </div>

      {/* Connection picker (only on source-tables tab) */}
      {tab === TAB_TABLES && (
        <select className="select mb-2 text-[12px]" value={conn} onChange={(e) => setConn(e.target.value)} data-testid="dx-conn">
          <option value="">— pick connection —</option>
          {conns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      )}

      {/* List */}
      <div className="flex-1 overflow-auto text-[12.5px] border rounded" style={{ borderColor: "var(--c-border)" }}>
        {tab === TAB_DATASETS && (
          <>
            {filteredDatasets.length === 0 && <div className="p-3" style={{ color: "var(--c-text-muted)" }}>No datasets.</div>}
            {filteredDatasets.map((d) => {
              const id = d.id || `demo:${d.demo_key}`;
              const isOpen = expandedDs === id;
              const fields = d.fields || [];
              const dims = fields.filter((f) => f.role !== "measure");
              const measures = fields.filter((f) => f.role === "measure");
              return (
                <div key={id} className="border-b" style={{ borderColor: "var(--c-border)" }} data-testid={`dx-ds-${id}`}>
                  <button className="w-full flex items-center gap-1.5 px-2 py-1.5 hover:bg-slate-50 text-left"
                          onClick={() => setExpandedDs(isOpen ? null : id)} data-testid={`dx-ds-toggle-${id}`}>
                    {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                    <Boxes size={12} strokeWidth={1.7} />
                    <span className="flex-1">{d.name}</span>
                    {d.kind === "excel" && (
                      <span className="text-[9.5px] px-1.5 py-[1px] rounded font-mono" style={{ background: "#fef3c7", color: "#854d0e" }} title="Excel-backed dataset">XL</span>
                    )}
                    <span className="text-[10.5px]" style={{ color: "var(--c-text-muted)" }}>{fields.length} fields</span>
                  </button>
                  {isOpen && (
                    <div className="px-3 pb-2">
                      <div className="flex items-center gap-2 mb-1">
                        <button className="btn btn-ghost text-[11px]" onClick={() => showDatasetPreview(d)} data-testid={`dx-ds-preview-${id}`}><Eye size={11} /> Preview 50 rows</button>
                      </div>
                      {dims.length > 0 && <div className="font-mono text-[10.5px] mt-1 mb-0.5" style={{ color: "var(--c-text-muted)" }}>DIMENSIONS</div>}
                      {dims.map((f) => (
                        <FieldRow key={f.name} field={f} role="dimension" onUseDim={() => onPickDimension?.(id, f)} onUseMsr={() => onPickMeasure?.(id, f)} />
                      ))}
                      {measures.length > 0 && <div className="font-mono text-[10.5px] mt-2 mb-0.5" style={{ color: "var(--c-text-muted)" }}>MEASURES</div>}
                      {measures.map((f) => (
                        <FieldRow key={f.name} field={f} role="measure" onUseDim={() => onPickDimension?.(id, f)} onUseMsr={() => onPickMeasure?.(id, f)} />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}

        {tab === TAB_TABLES && (
          <>
            {!conn && <div className="p-3" style={{ color: "var(--c-text-muted)" }}>Pick a connection to browse source tables.</div>}
            {conn && tablesLoading && <div className="p-3 flex items-center gap-2" style={{ color: "var(--c-text-muted)" }}><Loader2 size={12} className="animate-spin" /> Loading tables…</div>}
            {tablesError && <div className="p-3" style={{ color: "#991b1b" }}><AlertCircle size={12} className="inline mr-1" />{tablesError}</div>}
            {!tablesLoading && !tablesError && filteredTables.slice(0, 200).map((t) => {
              const isOpen = expandedTbl === t.name;
              const cols = colsByTable[t.name] || [];
              const isLoading = colsLoading[t.name];
              const err = colsError[t.name];
              return (
                <div key={`${t.schema}.${t.name}`} className="border-b" style={{ borderColor: "var(--c-border)" }} data-testid={`dx-tbl-${t.name}`}>
                  <button className="w-full flex items-center gap-1.5 px-2 py-1.5 hover:bg-slate-50 text-left font-mono"
                          onClick={() => {
                            const next = isOpen ? null : t.name;
                            setExpandedTbl(next);
                            if (next && !COLUMNS_CACHE.has(`${conn}:${t.name}`)) loadColumns(t.name, t.schema);
                          }}
                          data-testid={`dx-tbl-toggle-${t.name}`}>
                    {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                    <Database size={12} strokeWidth={1.7} />
                    <span className="flex-1 truncate">{t.name}</span>
                  </button>
                  {isOpen && (
                    <div className="px-3 pb-2">
                      <div className="flex items-center gap-2 mb-1.5">
                        <button className="btn btn-ghost text-[11px]" onClick={() => showTablePreview(t)} data-testid={`dx-tbl-preview-${t.name}`}><Eye size={11} /> Preview 50 rows</button>
                        <button className="btn btn-outline text-[11px]" onClick={() => onCreateDatasetFromTable?.(conn, t)} data-testid={`dx-tbl-create-${t.name}`}><Plus size={11} /> Create dataset</button>
                      </div>
                      {isLoading && <div className="text-[11px]" style={{ color: "var(--c-text-muted)" }}>Loading columns…</div>}
                      {err && <div className="text-[11px]" style={{ color: "#991b1b" }}>{err}</div>}
                      {!isLoading && !err && cols.map((c) => (
                        <div key={c.name} className="flex items-center justify-between py-0.5 font-mono">
                          <span className="truncate">{c.name}</span>
                          <span className="text-[10px] ml-2" style={{ color: "var(--c-text-muted)" }}>{c.data_type}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            {!tablesLoading && filteredTables.length > 200 && (
              <div className="p-2 text-center text-[11px]" style={{ color: "var(--c-text-muted)" }}>Showing first 200. Refine the search to see more.</div>
            )}
          </>
        )}
      </div>

      {/* Preview drawer */}
      {previewing && (
        <PreviewDrawer p={previewing} onClose={() => setPreviewing(null)} />
      )}
    </div>
  );
}

function FieldRow({ field, role, onUseDim, onUseMsr }) {
  const isMeasure = role === "measure";
  return (
    <div className="flex items-center gap-2 py-0.5">
      <span className={`text-[9.5px] px-1.5 py-[1px] rounded font-mono ${isMeasure ? "bg-amber-100 text-amber-800" : "bg-sky-100 text-sky-800"}`}>{isMeasure ? "M" : "D"}</span>
      <span className="font-mono flex-1 truncate">{field.name}</span>
      <button className="btn btn-ghost text-[10px]" onClick={onUseDim} title="Use as dimension" data-testid={`dx-use-dim-${field.name}`}>dim</button>
      <button className="btn btn-ghost text-[10px]" onClick={onUseMsr} title="Use as measure" data-testid={`dx-use-msr-${field.name}`}>msr</button>
    </div>
  );
}

function PreviewDrawer({ p, onClose }) {
  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center bg-black/40" onClick={onClose} data-testid="dx-preview">
      <div className="bg-white w-[90vw] max-w-[1100px] h-[55vh] rounded-t-lg shadow-lg border" style={{ borderColor: "var(--c-border)" }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-2 border-b" style={{ borderColor: "var(--c-border)" }}>
          <div className="text-[13px] font-medium">Preview: {p.kind === "table" ? p.item.name : p.item.name}</div>
          <button className="btn btn-ghost" onClick={onClose} data-testid="dx-preview-close"><X size={14} /></button>
        </div>
        <div className="overflow-auto h-[calc(100%-44px)] p-2">
          {p.loading && <div className="p-3 flex items-center gap-2" style={{ color: "var(--c-text-muted)" }}><Loader2 size={12} className="animate-spin" /> Loading preview…</div>}
          {p.error && <div className="p-3" style={{ color: "#991b1b" }}><AlertCircle size={12} className="inline mr-1" />{p.error}</div>}
          {p.data?.columns && p.data?.rows && (
            <table className="tbl text-[11.5px]">
              <thead><tr>{p.data.columns.map((c) => <th key={c}>{c}</th>)}</tr></thead>
              <tbody>
                {p.data.rows.slice(0, 50).map((r, i) => (
                  <tr key={i}>
                    {r.map((v, j) => <td key={j} className="font-mono">{v === null ? "—" : String(v)}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
